import { useEffect, useRef, useState, type ReactNode } from "react";

interface LazyVisibleProps {
  children: ReactNode;
  /** Pixel buffer for prefetch before the section enters viewport. */
  rootMargin?: string;
  /** Reserved minimum height to avoid layout shift while unmounted. */
  minHeight?: number | string;
  className?: string;
  /** If true, render immediately on mount (use for above-the-fold). */
  eager?: boolean;
}

/**
 * Mounts its children only when the placeholder is near the viewport.
 * Used to defer heavy below-the-fold sections on the landing page —
 * avoids running framer/gsap effects and downloading images for content
 * the user has not yet scrolled to.
 */
export function LazyVisible({
  children,
  rootMargin = "400px 0px",
  minHeight = 600,
  className,
  eager = false,
}: LazyVisibleProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(eager);

  useEffect(() => {
    if (visible) return;
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    // No mobile usamos uma margem menor: a seção só monta quando está quase
    // entrando na tela, deixando o carregamento inicial mais leve.
    const isMobile =
      typeof window.matchMedia === "function" && window.matchMedia("(max-width: 767px)").matches;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: isMobile ? "900px 0px" : "1200px 0px" },
    );

    io.observe(node);
    return () => io.disconnect();
  }, [rootMargin, visible]);

  return (
    <div
      ref={ref}
      className={className}
      style={visible ? undefined : { minHeight }}
    >
      {visible ? children : null}
    </div>
  );
}
